import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Descriptions, Tag, Button, Space, Table, Modal, message, Progress, Typography, Alert,
} from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
  getImportJob,
  validateImportJob,
  executeImportJob,
  cancelImportJob,
  type ImportJob,
  type ImportRowError,
} from '@/api/dataManagementApi';
import { useAuth } from '@/hooks/useAuth';

const { Title } = Typography;

const statusColors: Record<string, string> = {
  pending: 'default',
  validating: 'processing',
  validated: 'blue',
  importing: 'processing',
  completed: 'green',
  failed: 'red',
  cancelled: 'orange',
};

export default function ImportJobDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [job, setJob] = useState<ImportJob | null>(null);
  const [loading, setLoading] = useState(true);

  const canWrite = hasPermission('data_management', 'write');

  const loadJob = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await getImportJob(id);
      if (res.data?.success && res.data.data) {
        setJob(res.data.data);
      }
    } catch {
      message.error(t('app.error'));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    loadJob();
  }, [loadJob]);

  const handleValidate = () => {
    Modal.confirm({
      title: t('app.confirm'),
      content: t('data_management.import.validate'),
      okText: t('app.yes'),
      cancelText: t('app.no'),
      onOk: async () => {
        try {
          await validateImportJob(id!);
          message.success(t('data_management.import.validate_success'));
          loadJob();
        } catch {
          message.error(t('app.error'));
        }
      },
    });
  };

  const handleExecute = () => {
    Modal.confirm({
      title: t('app.confirm'),
      content: t('data_management.import.execute'),
      okText: t('app.yes'),
      cancelText: t('app.no'),
      onOk: async () => {
        try {
          await executeImportJob(id!);
          message.success(t('data_management.import.execute_success'));
          loadJob();
        } catch {
          message.error(t('app.error'));
        }
      },
    });
  };

  const handleCancel = () => {
    Modal.confirm({
      title: t('app.confirm'),
      content: t('data_management.import.cancel'),
      okText: t('app.yes'),
      cancelText: t('app.no'),
      okType: 'danger',
      onOk: async () => {
        try {
          await cancelImportJob(id!);
          message.success(t('data_management.import.cancel_success'));
          loadJob();
        } catch {
          message.error(t('app.error'));
        }
      },
    });
  };

  if (loading || !job) return null;

  const progressPct = job.total_rows > 0
    ? Math.round((job.processed_rows / job.total_rows) * 100)
    : 0;

  const errorColumns = [
    { title: '#', dataIndex: 'row_number', key: 'row_number', width: 60 },
    { title: t('data_management.import.field.name'), dataIndex: 'field', key: 'field', width: 140 },
    { title: t('data_management.import.field.value', 'Value'), dataIndex: 'value', key: 'value', width: 160, render: (v: string | null) => v ?? '-' },
    { title: t('data_management.import.field.error', 'Error'), dataIndex: 'error', key: 'error' },
  ];

  const mappingColumns = [
    { title: t('data_management.import.field.source_column', 'Source Column'), dataIndex: 'source_column', key: 'source_column' },
    { title: t('data_management.import.field.target_field', 'Target Field'), dataIndex: 'target_field', key: 'target_field' },
    { title: t('data_management.import.field.default_value', 'Default'), dataIndex: 'default_value', key: 'default_value', render: (v: string | null) => v ?? '-' },
    {
      title: t('data_management.import.field.is_required', 'Required'),
      dataIndex: 'is_required',
      key: 'is_required',
      render: (v: boolean) => <Tag color={v ? 'red' : 'default'}>{v ? t('app.yes') : t('app.no')}</Tag>,
    },
  ];

  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/data-management/imports')}>
          {t('app.back')}
        </Button>
        {canWrite && job.status === 'pending' && (
          <Button type="primary" onClick={handleValidate}>
            {t('data_management.import.validate')}
          </Button>
        )}
        {canWrite && job.status === 'validated' && (
          <Button type="primary" onClick={handleExecute}>
            {t('data_management.import.execute')}
          </Button>
        )}
        {canWrite && ['validating', 'importing'].includes(job.status) && (
          <Button danger onClick={handleCancel}>
            {t('data_management.import.cancel')}
          </Button>
        )}
      </Space>

      <Title level={4}>{job.name}</Title>

      {job.error_message && (
        <Alert type="error" message={job.error_message} style={{ marginBottom: 16 }} showIcon />
      )}

      <Descriptions bordered column={2} style={{ marginBottom: 24 }}>
        <Descriptions.Item label={t('data_management.import.field.name')}>{job.name}</Descriptions.Item>
        <Descriptions.Item label={t('data_management.import.field.status')}>
          <Tag color={statusColors[job.status] ?? 'default'}>
            {t(`data_management.import.statuses.${job.status}`, job.status)}
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label={t('data_management.import.field.description')} span={2}>
          {job.description || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('data_management.import.field.target_module')}>
          {t(`data_management.import.modules.${job.target_module}`, job.target_module)}
        </Descriptions.Item>
        <Descriptions.Item label={t('data_management.import.field.file_format')}>
          {t(`data_management.import.formats.${job.file_format}`, job.file_format)}
        </Descriptions.Item>
        <Descriptions.Item label={t('data_management.import.field.file_name')}>{job.file_name}</Descriptions.Item>
        <Descriptions.Item label={t('data_management.import.field.file_size')}>
          {job.file_size > 0 ? `${(job.file_size / 1024).toFixed(1)} KB` : '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('data_management.import.field.duplicate_strategy')}>
          {t(`data_management.import.duplicate_strategies.${job.duplicate_strategy}`, job.duplicate_strategy)}
        </Descriptions.Item>
        <Descriptions.Item label={t('data_management.import.field.owner')}>{job.owner_id}</Descriptions.Item>
        <Descriptions.Item label={t('data_management.import.field.started_at')}>
          {job.started_at ? new Date(job.started_at).toLocaleString() : '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('data_management.import.field.completed_at')}>
          {job.completed_at ? new Date(job.completed_at).toLocaleString() : '-'}
        </Descriptions.Item>
      </Descriptions>

      {job.total_rows > 0 && (
        <>
          <Title level={5}>{t('data_management.import.field.total_rows')}</Title>
          <Progress percent={progressPct} status={job.status === 'failed' ? 'exception' : undefined} />
          <Space style={{ marginTop: 8, marginBottom: 24 }}>
            <Tag>{t('data_management.import.field.total_rows')}: {job.total_rows}</Tag>
            <Tag color="green">{t('data_management.import.field.success_rows')}: {job.success_rows}</Tag>
            <Tag color="red">{t('data_management.import.field.error_rows')}: {job.error_rows}</Tag>
            <Tag color="orange">{t('data_management.import.field.duplicate_rows')}: {job.duplicate_rows}</Tag>
          </Space>
        </>
      )}

      {job.field_mappings?.length > 0 && (
        <>
          <Title level={5}>{t('data_management.import.field_mappings_title', 'Field Mappings')}</Title>
          <Table
            dataSource={job.field_mappings}
            columns={mappingColumns}
            rowKey="source_column"
            pagination={false}
            size="small"
            style={{ marginBottom: 24 }}
          />
        </>
      )}

      {job.errors?.length > 0 && (
        <>
          <Title level={5}>{t('data_management.import.field.error_rows')} ({job.errors.length})</Title>
          <Table
            dataSource={job.errors}
            columns={errorColumns}
            rowKey={(r: ImportRowError) => `${r.row_number}-${r.field}`}
            pagination={{ pageSize: 20 }}
            size="small"
          />
        </>
      )}
    </div>
  );
}
